/* Product page form: size buttons, quantity stepper, Add to Cart total,
   mobile buy bar and restock alerts.

   Markup: snippets/product-form ([data-product-buy]), product-buy and
   product-size-selector. Styles: assets/product-size-selector.css.

   The theme still owns the variant (Shopify.OptionSelectors on the hidden
   select[name="id"]) and the add to cart request (app.js). This script:
   - picks the variant when a size button is chosen, by setting the hidden
     option selector the way the swatches do;
   - stops Add to Cart until a size is chosen (capture-phase listener on the
     form, so app.js's handler never sees the click);
   - caps the quantity at stock minus what's already in the cart (/cart.js);
   - replaces the theme's generic warning after a 422 stock error;
   - re-applies the "Add to cart · $total" label after the theme resets it.

   This file loads (deferred) before jQuery and app.js, so start-up waits
   until the theme's own DOMContentLoaded set-up has run. */

(function () {
  'use strict';

  var MOBILE_QUERY = window.matchMedia('(max-width: 798px)');
  var instances = [];
  var themeBound = false;

  /* Restock adapter
     ------------------------------------------------------------------------
     Subscribes an email address to back-in-stock alerts for each variant,
     through Klaviyo's client API (the Klaviyo onsite embed provides the
     public key). The alert emails are sent by the Back in Stock flow in
     Klaviyo. Resolves when every subscription is accepted and rejects
     otherwise. To use another provider, replace this function. */
  var KLAVIYO_REVISION = '2024-10-15';

  function klaviyoCompanyId() {
    var script = document.querySelector('script[src*="klaviyo.js"][src*="company_id="]');
    var match = script && script.src.match(/[?&]company_id=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function subscribeToRestock(email, variantIds) {
    var companyId = klaviyoCompanyId();
    if (!companyId || !window.fetch) {
      return Promise.reject(new Error('Klaviyo is not available on this page'));
    }
    var url = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId);
    return Promise.all(variantIds.map(function (variantId) {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email } } },
              channels: ['EMAIL']
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
            }
          }
        })
      }).then(function (response) {
        if (!response.ok) throw new Error('Klaviyo responded ' + response.status);
      });
    }));
  }

  /* Cart quantities per variant id, shared by every form on the page */
  var cartQuantities = null;
  var cartRequest = null;
  var cartRefreshTimer = null;

  function fetchCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    cartRequest = fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var quantities = {};
        cart.items.forEach(function (item) {
          quantities[item.variant_id] = (quantities[item.variant_id] || 0) + item.quantity;
        });
        cartQuantities = quantities;
        return quantities;
      })
      .catch(function () {
        return cartQuantities || {};
      });
    return cartRequest;
  }

  function refreshCartSoon() {
    clearTimeout(cartRefreshTimer);
    cartRefreshTimer = setTimeout(function () {
      fetchCart().then(function () {
        instances.forEach(function (instance) { instance.refresh(); });
      });
    }, 150);
  }

  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    return (cents / 100).toFixed(2);
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return values[key] != null ? values[key] : match;
    });
  }

  /* One product form */
  function ProductBuy(wrapper) {
    var dataElement = wrapper.querySelector('[data-product-buy-data]');
    this.wrapper = wrapper;
    this.data = JSON.parse(dataElement.textContent);
    this.form = wrapper.querySelector('form');
    this.idSelect = wrapper.querySelector('select[name="id"]');
    this.sizeGroup = wrapper.querySelector('[data-size-selector]');
    this.radios = this.sizeGroup ? Array.prototype.slice.call(this.sizeGroup.querySelectorAll('input[type="radio"]')) : [];
    this.message = wrapper.querySelector('[data-buy-message]');
    this.quantity = wrapper.querySelector('[data-quantity]');
    this.minus = wrapper.querySelector('[data-step="minus"]');
    this.plus = wrapper.querySelector('[data-step="plus"]');
    this.button = wrapper.querySelector('.product-buy__cta .add_to_cart');
    this.buttonText = this.button && this.button.querySelector('.text');
    this.bar = wrapper.querySelector('[data-product-buy-bar]');
    this.moneyFormat = wrapper.getAttribute('data-money-format');
    this.chosen = null;
    this.promptShown = false;
    this.pendingAdd = 0;

    var checked = this.radios.filter(function (radio) { return radio.checked; })[0];
    if (checked) this.chosen = checked.value;

    this.bindSizes();
    this.bindStepper();
    this.bindAddToCart();
    this.bindRestock();
    this.bindBar();

    var self = this;
    // selectCallback dispatches a non-bubbling change on the hidden select
    // before it resets the button, so update after it has finished
    if (this.idSelect) {
      this.idSelect.addEventListener('change', function () {
        setTimeout(function () { self.refresh(); }, 0);
      });
    }

    // Re-fit the label when the button's width changes
    if (window.ResizeObserver && this.button) {
      var buttonWidth = 0;
      new ResizeObserver(function (entries) {
        var width = Math.round(entries[0].contentRect.width);
        if (width === buttonWidth) return;
        buttonWidth = width;
        self.renderLabel();
      }).observe(this.button);
    }

    this.refresh();
    fetchCart().then(function () { self.refresh(); });
  }

  ProductBuy.prototype.currentVariant = function () {
    var id = this.data.sizeMode ? this.chosen : this.idSelect && this.idSelect.value;
    return (id && this.data.variants[id]) || null;
  };

  // Highest quantity that can still be added: stock minus what's in the cart
  // for tracked variants that don't oversell; otherwise no limit
  ProductBuy.prototype.maxFor = function (variant) {
    if (!variant || !variant.tracked || !variant.deny) return Infinity;
    var inCart = (cartQuantities && cartQuantities[variant.id]) || 0;
    return Math.max(0, variant.quantity - inCart);
  };

  ProductBuy.prototype.stockMessage = function (variant, max) {
    var strings = this.data.strings;
    var size = variant.size;
    if (max <= 0) return size ? fill(strings.cartFull, { size: size }) : strings.cartFullPlain;
    if (max === 1) return size ? fill(strings.lastOne, { size: size }) : strings.lastOnePlain;
    return fill(size ? strings.onlyAvailable : strings.onlyAvailablePlain, { count: max, size: size });
  };

  ProductBuy.prototype.setMessage = function (text, isError) {
    if (!this.message) return;
    if (this.message.textContent !== text) this.message.textContent = text;
    this.message.classList.toggle('product-buy__message--error', !!(text && isError));
  };

  // Brings the stepper, message and button label in line with the current
  // size, stock and cart. `limitHit` shows the stock message even when the
  // quantity wasn't lowered (+ pressed at the limit, 422 from the cart).
  ProductBuy.prototype.refresh = function (options) {
    options = options || {};
    var variant = this.currentVariant();
    var max = this.maxFor(variant);
    var lowered = false;

    if (this.quantity) {
      var quantity = parseInt(this.quantity.value, 10);
      if (isNaN(quantity) || quantity < 1) quantity = 1;
      if (quantity > max && max >= 1) {
        quantity = max;
        lowered = true;
      }
      if (String(quantity) !== this.quantity.value) this.quantity.value = quantity;
      if (isFinite(max) && max >= 1) {
        this.quantity.max = max;
      } else {
        this.quantity.removeAttribute('max');
      }
      this.minus.disabled = quantity <= 1;
      this.plus.setAttribute('aria-disabled', quantity >= max ? 'true' : 'false');
    }

    if (this.data.sizeMode && !this.chosen) {
      this.setMessage(this.promptShown ? this.data.strings.chooseSize : '', true);
    } else if (variant && variant.available && isFinite(max) && (max <= 1 || lowered || options.limitHit)) {
      this.setMessage(this.stockMessage(variant, max), options.limitHit || max <= 0);
    } else if (!options.keepMessage) {
      this.setMessage('');
    }

    this.renderLabel();
  };

  ProductBuy.prototype.renderLabel = function () {
    if (!this.buttonText) return;
    var button = this.button;
    var text;

    if (this.data.sizeMode && !this.chosen) {
      // The hidden select may hold a sold-out variant (e.g. from ?variant=);
      // the button stays usable so it can ask for a size
      if (this.data.anyAvailable) {
        button.classList.remove('disabled');
        button.removeAttribute('disabled');
        button.parentNode.classList.remove('product-is-unavailable');
      }
      text = button.getAttribute('data-label-empty');
    } else {
      var variant = this.currentVariant();
      // Sold out or unavailable: keep the theme's label
      if (!variant || !variant.available) return;
      var quantity = parseInt(this.quantity && this.quantity.value, 10) || 1;
      var price = formatMoney(variant.price * quantity, this.moneyFormat);
      text = fill(button.getAttribute('data-label-total'), { price: price });
      var shortText = fill(button.getAttribute('data-label-total-short'), { price: price });

      // "Add · $X" on mobile, and wherever "Add to cart · $X" doesn't fit
      // on one line (the label doesn't wrap)
      if (MOBILE_QUERY.matches) {
        text = shortText;
      } else {
        this.buttonText.textContent = text;
        if (button.clientWidth && button.scrollWidth > button.clientWidth) text = shortText;
      }
    }

    if (this.buttonText.textContent !== text) this.buttonText.textContent = text;
  };

  ProductBuy.prototype.bindSizes = function () {
    var self = this;
    this.radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) self.choose(radio.value);
      });
    });
  };

  ProductBuy.prototype.choose = function (variantId) {
    var variant = this.data.variants[variantId];
    this.chosen = variantId;
    this.promptShown = false;

    // Select the variant through the theme's option selector, as the
    // swatches do, so price, gallery, pickup and ?variant= follow
    var optionSelector = this.form && this.form.querySelector('.single-option-selector');
    if (optionSelector && variant) {
      optionSelector.value = variant.option;
      optionSelector.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (this.idSelect) {
      this.idSelect.value = variantId;
    }
    this.refresh();
  };

  ProductBuy.prototype.promptForSize = function () {
    this.promptShown = true;
    this.refresh();
    var target = this.radios[0];
    if (target) {
      target.focus({ preventScroll: true });
      this.sizeGroup.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  ProductBuy.prototype.bindStepper = function () {
    if (!this.quantity) return;
    var self = this;

    this.minus.addEventListener('click', function () {
      var quantity = parseInt(self.quantity.value, 10) || 1;
      if (quantity > 1) self.quantity.value = quantity - 1;
      self.refresh();
    });

    this.plus.addEventListener('click', function () {
      var quantity = parseInt(self.quantity.value, 10) || 1;
      if (quantity >= self.maxFor(self.currentVariant())) {
        self.refresh({ limitHit: true });
        return;
      }
      self.quantity.value = quantity + 1;
      self.refresh();
    });

    this.quantity.addEventListener('change', function () {
      var quantity = parseInt(self.quantity.value, 10);
      self.refresh({ limitHit: quantity > self.maxFor(self.currentVariant()) });
    });
  };

  ProductBuy.prototype.bindAddToCart = function () {
    if (!this.form || !this.button) return;
    var self = this;

    function needsSize(event) {
      if (!self.data.sizeMode || self.chosen) return false;
      event.preventDefault();
      event.stopPropagation();
      self.promptForSize();
      return true;
    }

    this.form.addEventListener('click', function (event) {
      if (!event.target.closest || !event.target.closest('.product-buy__cta .add_to_cart')) return;
      if (!needsSize(event)) self.pendingAdd = Date.now();
    }, true);

    this.form.addEventListener('submit', needsSize, true);
  };

  // A 422 from /cart/add.js for this form: stock ran out or the cart already
  // holds the rest. Replace the theme's generic warning with the stock message.
  ProductBuy.prototype.onAddError = function (xhr) {
    if (!this.pendingAdd || Date.now() - this.pendingAdd > 30000) return;
    this.pendingAdd = 0;
    if (xhr.status !== 422) return;

    var response = {};
    try { response = JSON.parse(xhr.responseText); } catch (error) { /* keep the theme's warning */ }
    if (response.errors && response.errors.email) return;

    var variant = this.currentVariant();
    if (!variant || !variant.tracked || !variant.deny) return;

    var warning = this.form.nextElementSibling;
    if (warning && warning.matches('p.warning')) warning.parentNode.removeChild(warning);

    var self = this;
    fetchCart().then(function () { self.refresh({ limitHit: true }); });
  };

  ProductBuy.prototype.bindRestock = function () {
    var restock = this.wrapper.querySelector('[data-size-restock]');
    if (!restock) return;

    var toggle = restock.querySelector('[data-restock-toggle]');
    var panel = restock.querySelector('[data-restock-panel]');
    var email = restock.querySelector('[data-restock-email]');
    var error = restock.querySelector('[data-restock-error]');
    var submit = restock.querySelector('[data-restock-submit]');
    var success = restock.querySelector('[data-restock-success]');
    var strings = this.data.strings;

    toggle.addEventListener('click', function () {
      var open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
      restock.classList.toggle('size-restock--open', open);
    });

    function showError(text, focusTarget) {
      error.textContent = text;
      if (focusTarget) focusTarget.focus();
    }

    function send() {
      var chips = Array.prototype.slice.call(panel.querySelectorAll('input[type="checkbox"]:checked'));
      var address = email.value.trim();

      if (!chips.length) {
        showError(strings.restockNoSize, panel.querySelector('input[type="checkbox"]'));
        return;
      }
      if (!address || !email.checkValidity() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
        showError(strings.restockInvalidEmail, email);
        return;
      }

      showError('');
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');

      var ids = chips.map(function (chip) { return chip.value; });
      var sizes = chips.map(function (chip) { return chip.getAttribute('data-size'); }).join(', ');

      subscribeToRestock(address, ids).then(function () {
        success.textContent = fill(strings.restockSuccess, { sizes: sizes });
        success.hidden = false;
        panel.hidden = true;
        toggle.hidden = true;
        restock.classList.remove('size-restock--open');
        restock.classList.add('size-restock--done');
      }, function () {
        showError(strings.restockError);
      }).then(function () {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      });
    }

    submit.addEventListener('click', send);
    email.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        send();
      }
    });
  };

  // Below 799px the stepper and button are fixed to the bottom of the screen;
  // the page gets matching bottom padding (styles in the CSS asset)
  ProductBuy.prototype.bindBar = function () {
    if (!this.bar) return;
    var bar = this.bar;
    document.body.classList.add('has-product-buy-bar');

    function measure() {
      document.body.style.setProperty('--product-buy-bar-height', bar.offsetHeight + 'px');
    }
    measure();
    if (window.ResizeObserver) new ResizeObserver(measure).observe(bar);
  };

  function init(root) {
    var wrappers = (root || document).querySelectorAll('[data-product-buy]');
    Array.prototype.forEach.call(wrappers, function (wrapper) {
      if (wrapper.hasAttribute('data-product-buy-init') || !wrapper.querySelector('[data-product-buy-data]')) return;
      wrapper.setAttribute('data-product-buy-init', '');
      instances.push(new ProductBuy(wrapper));
    });
  }

  function bindTheme() {
    if (themeBound) return;
    themeBound = true;

    MOBILE_QUERY.addEventListener('change', function () {
      instances.forEach(function (instance) { instance.renderLabel(); });
    });

    if (!window.jQuery) return;
    window.jQuery(document).on('ajaxComplete', function (event, xhr, settings) {
      if (/\/cart\/(add|change|update|clear)/.test(settings.url)) refreshCartSoon();
    });
    window.jQuery(document).on('ajaxSuccess', function (event, xhr, settings) {
      if (!/\/cart\/add/.test(settings.url)) return;
      instances.forEach(function (instance) { instance.pendingAdd = 0; });
    });
    window.jQuery(document).on('ajaxError', function (event, xhr, settings) {
      if (!/\/cart\/add/.test(settings.url)) return;
      instances.forEach(function (instance) { instance.onAddError(xhr); });
    });
  }

  function start() {
    // After the theme's DOMContentLoaded handlers (productPage.init and
    // runOptionSelector)
    setTimeout(function () {
      bindTheme();
      init(document);
    }, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (event) {
    setTimeout(function () { init(event.target); }, 0);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    instances = instances.filter(function (instance) {
      return !event.target.contains(instance.wrapper);
    });
    var barLeft = instances.some(function (instance) { return instance.bar; });
    if (!barLeft) document.body.classList.remove('has-product-buy-bar');
  });
})();
